import { responsiveTokenVars, semanticTokenVars } from '@tundr/ds-tokens';
import { responsiveTokens } from '@tundr/ds-tokens/internal';
import type { StyleRule } from '@vanilla-extract/css';

/**
 * Font family and weight constants.
 *
 * These are not part of the generated token contract (the Figma export
 * does not include font tokens). Values are hardcoded until the token
 * pipeline is extended to cover fonts.
 */
export const fontFamilyVars = {
  body: "'Inter Variable', 'Inter', sans-serif",
  title: "'Sofia Sans Variable', 'Sofia Sans', sans-serif",
} as const;

export const fontWeightVars = {
  regular: '400',
  semiBold: '500',
  bold: '700',
  extraBold: '800',
} as const;
export const fontSizeVars = responsiveTokenVars.fontSize;
export const lineHeightVars = responsiveTokenVars.lineHeight;

const fontSizeScale = responsiveTokens.fontSize;

/** Enforces full key-parity with fontSizeScale (roles + levels). */
const lineHeightScale: {
  [K in keyof typeof fontSizeScale]: Record<
    keyof (typeof fontSizeScale)[K],
    string
  >;
} = responsiveTokens.lineHeight;

export type TypographyRole = keyof typeof fontSizeScale;
export type HeadingLevel = keyof (typeof fontSizeScale)['heading'];
export type BodyLevel = keyof (typeof fontSizeScale)['body'];

export function getFontSize(role: 'heading', level: HeadingLevel): string;
export function getFontSize(role: 'body', level: BodyLevel): string;
export function getFontSize(
  role: TypographyRole,
  level: HeadingLevel | BodyLevel,
): string {
  if (role === 'heading') {
    return fontSizeScale.heading[level as HeadingLevel];
  }

  return fontSizeScale.body[level as BodyLevel];
}

export function getLineHeight(role: 'heading', level: HeadingLevel): string;
export function getLineHeight(role: 'body', level: BodyLevel): string;
export function getLineHeight(
  role: TypographyRole,
  level: HeadingLevel | BodyLevel,
): string {
  if (role === 'heading') {
    return lineHeightScale.heading[level as HeadingLevel];
  }

  return lineHeightScale.body[level as BodyLevel];
}

export const headingTextPreset = {
  fontFamily: fontFamilyVars.title,
  fontSize: fontSizeVars.heading.h2,
  lineHeight: lineHeightVars.heading.h2,
  fontWeight: fontWeightVars.extraBold,
  color: semanticTokenVars.content.default.default,
} as const satisfies StyleRule;

export const titleTextPreset = {
  fontFamily: fontFamilyVars.body,
  fontSize: fontSizeVars.body.body1,
  lineHeight: lineHeightVars.body.body1,
  fontWeight: fontWeightVars.bold,
  color: semanticTokenVars.content.default.default,
} as const satisfies StyleRule;

export const bodyTextPreset = {
  fontFamily: fontFamilyVars.body,
  fontSize: fontSizeVars.body.body1,
  lineHeight: lineHeightVars.body.body1,
  fontWeight: fontWeightVars.regular,
  color: semanticTokenVars.content.default.default,
} as const satisfies StyleRule;

export const semiboldTextPreset = {
  fontFamily: fontFamilyVars.body,
  fontSize: fontSizeVars.body.body1,
  lineHeight: lineHeightVars.body.body1,
  fontWeight: fontWeightVars.semiBold,
  color: semanticTokenVars.content.default.default,
} as const satisfies StyleRule;

export const captionTextPreset = {
  fontFamily: fontFamilyVars.body,
  fontSize: fontSizeVars.body.body2,
  lineHeight: lineHeightVars.body.body2,
  fontWeight: fontWeightVars.regular,
  color: semanticTokenVars.content.muted.default,
} as const satisfies StyleRule;

export const smallTextPreset = {
  fontFamily: fontFamilyVars.body,
  fontSize: fontSizeVars.body.body2,
  lineHeight: lineHeightVars.body.body2,
  fontWeight: fontWeightVars.regular,
  color: semanticTokenVars.content.default.default,
} as const satisfies StyleRule;
